/**
 * SchadenJeSparteSection - Schadenverlauf je Sparte (P2 hardening Q15). One row per (Kunde, IGB2B
 * Branchencode): Anzahl / Art / Betrag der Schaeden over the Zeitraum (default rolling 5 years).
 *
 * Rendered inside the «Schaden- und Vertragshistorie» card of the Kunde Risikoprofil tab (below the
 * two remaining catalog fields). Add / inline-edit / two-step-remove rows; «Uebernehmen» pre-fills a
 * row from the read-only Schadenauskunft hint; the retired flat fields show as a read-only Altbestand
 * note with an explicit «Altbestand verwerfen». All controls are gated on `canWrite`.
 */

import { useState } from 'react';
import {
  KUNDE_SCHADEN_QUELLE_LABELS,
  SCHADEN_ART_VORSCHLAEGE,
} from '@brokly/shared';
import type { KundeSchadenverlaufResponse, KundeSchadenSpartePutBody } from '@brokly/shared';
import { Button } from '../../../../components/ui/Button';
import { chf, dateShortDe } from '../../../../lib/format';

type Draft = {
  anzahl_schaeden: string;
  art_schaeden: string;
  schadensumme: string;
  zeitraum_von: string;
  zeitraum_bis: string;
  notiz: string;
};

const EMPTY_DRAFT = (von: string, bis: string): Draft => ({
  anzahl_schaeden: '',
  art_schaeden: '',
  schadensumme: '',
  zeitraum_von: von,
  zeitraum_bis: bis,
  notiz: '',
});

function isNonNegInt(s: string): boolean {
  return /^\d+$/.test(s.trim());
}

export function SchadenJeSparteSection({
  data,
  canWrite,
  isSaving,
  onPut,
  onRemove,
  onClearLegacy,
}: {
  data: KundeSchadenverlaufResponse;
  canWrite: boolean;
  isSaving: boolean;
  onPut: (branchencode: string, body: KundeSchadenSpartePutBody) => void;
  onRemove: (branchencode: string) => void;
  onClearLegacy: () => void;
}) {
  const { von, bis } = data.zeitraum_default;
  // The code currently in an inline edit (existing row) OR being added (not yet in items).
  const [editCode, setEditCode] = useState<string | null>(null);
  const [addCode, setAddCode] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT(von, bis));
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  const storedCount = data.items.filter((i) => i.row !== null).length;
  const storedCodes = new Set(data.items.filter((i) => i.row !== null).map((i) => i.branchencode));
  const addOptions = data.sparten_katalog.filter((o) => !storedCodes.has(o.branchencode));
  // Group the add-select options by familie_label.
  const byFamilie = new Map<string, typeof addOptions>();
  for (const o of addOptions) {
    const list = byFamilie.get(o.familie_label) ?? [];
    list.push(o);
    byFamilie.set(o.familie_label, list);
  }

  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));

  const beginEdit = (code: string) => {
    const item = data.items.find((i) => i.branchencode === code);
    const row = item?.row;
    setAddCode(null);
    setConfirmRemove(null);
    setEditCode(code);
    setDraft({
      anzahl_schaeden: row ? String(row.anzahl_schaeden) : '',
      art_schaeden: row?.art_schaeden ?? '',
      schadensumme: row?.schadensumme != null ? String(row.schadensumme) : '',
      zeitraum_von: row?.zeitraum_von ?? von,
      zeitraum_bis: row?.zeitraum_bis ?? bis,
      notiz: row?.notiz ?? '',
    });
  };

  const beginAdd = (code: string) => {
    setEditCode(null);
    setConfirmRemove(null);
    setAddCode(code);
    setDraft(EMPTY_DRAFT(von, bis));
  };

  const cancel = () => {
    setEditCode(null);
    setAddCode(null);
    setDraft(EMPTY_DRAFT(von, bis));
  };

  const saveRow = (code: string, isExisting: boolean) => {
    const body: KundeSchadenSpartePutBody = {
      anzahl_schaeden: Number(draft.anzahl_schaeden),
      art_schaeden: draft.art_schaeden.trim() === '' ? null : draft.art_schaeden,
      schadensumme: draft.schadensumme.trim() === '' ? null : Number(draft.schadensumme),
      zeitraum_von: draft.zeitraum_von,
      zeitraum_bis: draft.zeitraum_bis,
      notiz: draft.notiz.trim() === '' ? null : draft.notiz,
    };
    if (isExisting) {
      const row = data.items.find((i) => i.branchencode === code)?.row;
      if (row) {
        body.expected_updated_at = row.updated_at;
        // A manual edit overrides the Schadenauskunft value → the row is now broker-maintained.
        if (row.quelle === 'schadenauskunft') body.quelle = 'manuell';
      }
    }
    onPut(code, body);
    cancel();
  };

  const uebernehmen = (code: string, anzahl: number, summe: number | null) => {
    onPut(code, { anzahl_schaeden: anzahl, schadensumme: summe, quelle: 'schadenauskunft' });
  };

  const inputCls = 'w-full rounded-md border bg-background px-2 py-1 text-sm';

  // A plain render function (NOT a nested component) so the row reconciles in place on each
  // keystroke - a nested component would remount and detach the controlled inputs/buttons.
  const renderEditRow = (code: string, isExisting: boolean) => {
    const anzahlValid = isNonNegInt(draft.anzahl_schaeden);
    return (
      <tr key={`edit-${code}`} data-testid={`rp-schaden-edit-${code}`} className="border-b last:border-0 align-top">
        <td className="py-2 pr-3 font-medium text-foreground">
          {data.items.find((i) => i.branchencode === code)?.label ??
            data.sparten_katalog.find((o) => o.branchencode === code)?.label ??
            code}
        </td>
        <td className="py-2 pr-3">
          <input
            type="number"
            min={0}
            step={1}
            value={draft.anzahl_schaeden}
            onChange={(e) => set({ anzahl_schaeden: e.target.value })}
            className={inputCls}
            aria-label="Anzahl Schaeden"
            data-testid={`rp-schaden-input-anzahl-${code}`}
          />
        </td>
        <td className="py-2 pr-3">
          <input
            type="number"
            min={0}
            step={0.05}
            value={draft.schadensumme}
            onChange={(e) => set({ schadensumme: e.target.value })}
            className={inputCls}
            aria-label="Schadensumme CHF"
          />
        </td>
        <td className="py-2 pr-3">
          <input
            list="rp-schaden-art-vorschlaege"
            value={draft.art_schaeden}
            onChange={(e) => set({ art_schaeden: e.target.value })}
            className={inputCls}
            aria-label="Art der Schaeden"
          />
        </td>
        <td className="py-2 pr-3">
          <div className="flex flex-col gap-1">
            <input type="date" value={draft.zeitraum_von} onChange={(e) => set({ zeitraum_von: e.target.value })} className={inputCls} aria-label="Zeitraum von" />
            <input type="date" value={draft.zeitraum_bis} onChange={(e) => set({ zeitraum_bis: e.target.value })} className={inputCls} aria-label="Zeitraum bis" />
            <textarea
              value={draft.notiz}
              onChange={(e) => set({ notiz: e.target.value })}
              rows={1}
              placeholder="Notiz"
              className={inputCls}
              aria-label="Notiz"
            />
          </div>
        </td>
        <td className="py-2 pr-3 text-xs text-muted-foreground">-</td>
        <td className="py-2">
          <div className="flex flex-col gap-1">
            <Button
              size="sm"
              onClick={() => saveRow(code, isExisting)}
              disabled={isSaving || !anzahlValid}
              data-testid={`rp-schaden-save-${code}`}
            >
              {isSaving ? 'Speichern…' : 'Speichern'}
            </Button>
            <Button size="sm" variant="ghost" onClick={cancel}>Abbrechen</Button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <section className="mt-4 flex flex-col gap-3 border-t pt-4" data-testid="rp-schaden-sparte">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h5 className="text-sm font-semibold text-foreground">Schadenverlauf je Sparte</h5>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span>Zeitraum Standard: {dateShortDe(von)} bis {dateShortDe(bis)}</span>
          <span>{storedCount} Sparten erfasst</span>
        </div>
      </div>

      {data.legacy_aggregate !== null && (
        <div
          className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-status-warning/40 bg-status-warning/5 p-3 text-sm"
          data-testid="rp-schaden-legacy"
        >
          <span className="text-foreground">
            Altbestand (nicht je Sparte): Anzahl {data.legacy_aggregate.anzahl_schaeden_5j ?? '-'}, Art:{' '}
            {data.legacy_aggregate.art_schaeden_5j ?? '-'}. Bitte je Sparte neu erfassen.
          </span>
          {canWrite && (
            <Button size="sm" variant="outline" onClick={onClearLegacy} disabled={isSaving} data-testid="rp-schaden-legacy-verwerfen">
              Altbestand verwerfen
            </Button>
          )}
        </div>
      )}

      <datalist id="rp-schaden-art-vorschlaege">
        {SCHADEN_ART_VORSCHLAEGE.map((v) => (
          <option key={v} value={v} />
        ))}
      </datalist>

      {data.items.length === 0 && addCode === null ? (
        <p className="text-sm text-muted-foreground" data-testid="rp-schaden-empty">
          Noch keine Schaeden je Sparte erfasst.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead>
              <tr className="border-b text-xs text-muted-foreground">
                <th className="py-2 pr-3 font-medium">Sparte</th>
                <th className="py-2 pr-3 font-medium">Anzahl</th>
                <th className="py-2 pr-3 font-medium">Schadensumme CHF</th>
                <th className="py-2 pr-3 font-medium">Art</th>
                <th className="py-2 pr-3 font-medium">Zeitraum</th>
                <th className="py-2 pr-3 font-medium">Quelle</th>
                <th className="py-2 font-medium" aria-label="Aktionen" />
              </tr>
            </thead>
            <tbody>
              {data.items.map((item) => {
                const code = item.branchencode;
                if (editCode === code) return renderEditRow(code, true);
                const row = item.row;
                const hint = item.auskunft;
                const abweichung = row !== null && hint !== null && hint.anzahl !== row.anzahl_schaeden;
                return (
                  <tr key={code} data-testid={`rp-schaden-row-${code}`} className="border-b last:border-0 align-top">
                    <td className="py-2 pr-3">
                      <div className="font-medium text-foreground">{item.label}</div>
                      {abweichung && (
                        <span className="text-xs text-status-warning" data-testid={`rp-schaden-abweichung-${code}`}>
                          weicht von Schadenauskunft ab
                        </span>
                      )}
                    </td>
                    {row !== null ? (
                      <>
                        <td className="py-2 pr-3 text-foreground">{row.anzahl_schaeden}</td>
                        <td className="py-2 pr-3 text-foreground">{row.schadensumme === null ? '-' : chf(row.schadensumme)}</td>
                        <td className="py-2 pr-3 text-foreground">{row.art_schaeden ?? '-'}</td>
                        <td className="py-2 pr-3 text-foreground">
                          {dateShortDe(row.zeitraum_von)} - {dateShortDe(row.zeitraum_bis)}
                        </td>
                        <td className="py-2 pr-3 text-xs text-muted-foreground">{KUNDE_SCHADEN_QUELLE_LABELS[row.quelle]}</td>
                        <td className="py-2">
                          {canWrite && (
                            <div className="flex flex-col gap-1">
                              <Button size="sm" variant="outline" onClick={() => beginEdit(code)} data-testid={`rp-schaden-edit-btn-${code}`}>
                                Bearbeiten
                              </Button>
                              {confirmRemove === code ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => { onRemove(code); setConfirmRemove(null); }}
                                  data-testid={`rp-schaden-remove-confirm-${code}`}
                                >
                                  Wirklich entfernen
                                </Button>
                              ) : (
                                <Button size="sm" variant="ghost" onClick={() => setConfirmRemove(code)} data-testid={`rp-schaden-remove-${code}`}>
                                  Entfernen
                                </Button>
                              )}
                            </div>
                          )}
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="py-2 pr-3 text-xs text-muted-foreground" colSpan={4}>
                          {hint && (
                            <span>
                              Schadenauskunft: {hint.anzahl} Schaeden, {hint.summe === null ? '-' : chf(hint.summe)} ueber{' '}
                              {hint.policen} Police(n), Stand {dateShortDe(hint.retrieved_at)}
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-xs text-muted-foreground">Schadenauskunft</td>
                        <td className="py-2">
                          {canWrite && hint && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => uebernehmen(code, hint.anzahl, hint.summe)}
                              disabled={isSaving}
                              data-testid={`rp-schaden-uebernehmen-${code}`}
                            >
                              Uebernehmen
                            </Button>
                          )}
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
              {addCode !== null && renderEditRow(addCode, false)}
            </tbody>
          </table>
        </div>
      )}

      {canWrite && addCode === null && addOptions.length > 0 && (
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground" htmlFor="rp-schaden-add-select">Sparte hinzufuegen</label>
          <select
            id="rp-schaden-add-select"
            data-testid="rp-schaden-add-select"
            value=""
            onChange={(e) => { if (e.target.value) beginAdd(e.target.value); }}
            className="rounded-md border bg-background px-2 py-1 text-sm"
          >
            <option value="">Sparte waehlen…</option>
            {Array.from(byFamilie.entries()).map(([familieLabel, opts]) => (
              <optgroup key={familieLabel} label={familieLabel || 'Uebrige'}>
                {opts.map((o) => (
                  <option key={o.branchencode} value={o.branchencode}>
                    {o.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
      )}
    </section>
  );
}
